import {
  isMultileg,
  useUnifiedLiquidity,
  type MarketDataCardUnifiedLiquidityMarket,
  type Security,
} from '@talos/kyoko';
import { memo, useMemo, type RefObject } from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { MarketSelectorPanelOnly } from '../../MarketSelector/MarketSelectorPanelOnly';
import { useUnifiedLiquidityMarketSelectorExtensionFactory } from '../../MarketSelector/extensions/useUnifiedLiquidityMarketSelectorExtension';
import { useULSymbolsVisibility } from '../../MarketSelector/useULSymbolsVisibility';
import {
  useMarketDataCardMarketSelector,
  type PersistMarketDataCardMarketSelection,
} from '../useMarketDataCardMarketSelector';
import { MarketDataCardMultilegQuickMarketSelectorPanel } from './MarketDataCardMultilegQuickMarketSelectorPanel';

export interface MarketDataCardQuickMarketSelectorPanelProps {
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  unifiedLiquidityMarketSymbols: MarketDataCardUnifiedLiquidityMarket[];
  selectedAggregation: string | undefined;
  onPersistMarketSelection: (update: PersistMarketDataCardMarketSelection) => void;
  security: Security | undefined;
  ulEnabled: boolean;
  isOpen: boolean;
  onClose: () => void;
  panelAnchorRef: RefObject<HTMLDivElement>;
  isNarrow: boolean;
}

/**
 * Full Market Account selector implementation (inventory, MSS, enrichments, presets).
 * Mount only when the user opens the selector and all Market Account selector features are needed.
 */
export const MarketDataCardQuickMarketSelectorPanel = memo(function MarketDataCardQuickMarketSelectorPanel(
  props: MarketDataCardQuickMarketSelectorPanelProps
) {
  if (props.security && isMultileg(props.security)) {
    return <MarketDataCardMultilegQuickMarketSelectorPanel {...props} security={props.security} />;
  }

  return <MarketDataCardStandardQuickMarketSelectorPanel {...props} />;
});

function MarketDataCardStandardQuickMarketSelectorPanel({
  security,
  ulEnabled,
  selectedMarkets,
  selectedMarketAccounts,
  unifiedLiquidityMarketSymbols,
  selectedAggregation,
  onPersistMarketSelection,
  isOpen,
  onClose,
  panelAnchorRef,
  isNarrow,
}: MarketDataCardQuickMarketSelectorPanelProps) {
  const [ulSymbolsEnabled, setULSymbolsEnabled] = useULSymbolsVisibility(ulEnabled, security?.Symbol);
  const unifiedLiquidityResponse = useUnifiedLiquidity(security?.Symbol);
  const { enableUnifiedLiquidityMarketSymbols, unifiedLiquidityCombinePerpsAndCFDs } = useFeatureFlag();
  const showULSymbolsToggle = enableUnifiedLiquidityMarketSymbols && !!unifiedLiquidityResponse?.Tokens.length;
  const effectiveULEnabled = enableUnifiedLiquidityMarketSymbols ? showULSymbolsToggle && ulSymbolsEnabled : ulEnabled;

  const { marketSelector, marketSelectorItems } = useMarketDataCardMarketSelector({
    security,
    ulEnabled: effectiveULEnabled,
    selectedMarkets,
    selectedMarketAccounts,
    selectedAggregation,
    onPersistMarketSelection,
  });

  const unifiedLiquiditySelectionTarget = useMemo(
    () => ({
      type: 'external' as const,
      selections: unifiedLiquidityMarketSymbols,
      onChange: (nextUnifiedLiquidityMarketSymbols: MarketDataCardUnifiedLiquidityMarket[]) => {
        onPersistMarketSelection({
          selectedAggregation,
          selectedMarkets,
          selectedMarketAccounts,
          unifiedLiquidityMarketSymbols: nextUnifiedLiquidityMarketSymbols,
        });
      },
    }),
    [
      onPersistMarketSelection,
      selectedAggregation,
      selectedMarketAccounts,
      selectedMarkets,
      unifiedLiquidityMarketSymbols,
    ]
  );
  const unifiedLiquidityExtensionFactory = useUnifiedLiquidityMarketSelectorExtensionFactory({
    enabled: enableUnifiedLiquidityMarketSymbols && effectiveULEnabled,
    onEnabledChange: setULSymbolsEnabled,
    showToggle: showULSymbolsToggle,
    items: marketSelectorItems,
    symbol: security?.Symbol,
    unifiedLiquidityCombinePerpsAndCFDs,
    selectionTarget: unifiedLiquiditySelectionTarget,
  });
  const extensions = useMemo(
    () => (unifiedLiquidityExtensionFactory ? [unifiedLiquidityExtensionFactory] : []),
    [unifiedLiquidityExtensionFactory]
  );

  return (
    <MarketSelectorPanelOnly
      marketSelectorAPI={marketSelector}
      isOpen={isOpen}
      onClose={onClose}
      panelAnchorRef={panelAnchorRef}
      isNarrow={isNarrow}
      showCredentialsOnlyToggle
      extensions={extensions}
    />
  );
}
